<template>
  <main class="main color-groups">
    <div class="container">
      <LazyUiBreadCrumbsV2
        :items="[
          {
            title: t('сolor_groups'),
            link: localePath('/colors'),
          },
        ]"
      />

      <div class="color-groups-info">
        <div class="color-groups-info-image">
          <img
            alt=""
            class=""
            src="/img/color-group.svg"
          >
        </div>
        <div class="color-groups-info-content">
          <div class="content__header">
            <h1 class="content__header-name">
              {{ t('color_group_page.title') }}
            </h1>
          </div>
          <span class="content__header-description">
            {{ t('color_group_page.description') }}
          </span>
        </div>
      </div>

      <div class="color-groups__items">
        <h2 class="f fs gap12">
          <svg
            class="light"
            height="28"
            width="28"
          >
            <use xlink:href="/icons/sprite.svg#i-snowflake" />
          </svg>
          <span>{{ t('cool_tones') }}</span>
        </h2>

        <div class="color-groups__items-grid">
          <!--          <template -->

          <!--          > -->
          <!--                v-if="coolTones.includes(item.title)" -->
          <PagesColorsComponentsColGroupItem
            v-for="item in (computedCool || [])"
            :key="item.title"
            :image="getImage(item.title)"
            :item="item"
          />
          <!--          </template> -->
        </div>
      </div>

      <div class="color-groups__items">
        <h2 class="f fs gap12">
          <svg
            class="light"
            height="28"
            width="28"
          >
            <use xlink:href="/icons/sprite.svg#i-sun" />
          </svg>
          <span>{{ t('warm_tones') }}</span>
        </h2>

        <div class="color-groups__items-grid">
          <!--          <template -->

          <!--          > -->
          <!--                v-if="warmTones.includes(item.title)" -->
          <PagesColorsComponentsColGroupItem
            v-for="item in (computedWarm || [])"
            :key="item.title"
            :image="getImage(item.title)"
            :item="item"
          />
          <!--          </template> -->
        </div>
      </div>
    </div>
  </main>
</template>

<script lang="ts" setup>
import { useAlternateLinks } from '~/composables/alternate-links';
import { getColorsAndColors } from '~/utils/api';

const { locale, t } = useI18n();
const localePath = useLocalePath();
const config = useRuntimeConfig();
const siteHost = config.public.siteHost;
const { data, error } = await useAsyncData('getColorsAndColors', getColorsAndColors);
const coolTones = ['Aqua', 'Light Blue', 'Teal', 'Blue', 'Royal Blue', 'Navy Blue', 'Light Green', 'Green', 'Dark Green', 'White', 'Light Gray', 'Gray', 'Dark Gray', 'Black'];
const warmTones = ['Lime', 'Light Yellow', 'Bright Yellow', 'Yellow', 'Peach', 'Orange', 'Dark Orange', 'Red', 'Bright Red', 'Maroon', 'Pink', 'Violet', 'Purple', 'Beige', 'Light Brown', 'Golden Brown', 'Brown'];

if (data.value?.errors?.length || error.value) {
  throw createError({
    status: 404,
    statusText: `Colors page error: ${data.value?.errors?.length || error.value}`,
  });
}
const computedCool = computed(() => {
  return data.value?.data?.itemsCountByColors
    ?.filter(item => coolTones.includes(item.title))
    ?.sort((a, b) => coolTones.indexOf(a.title) - coolTones.indexOf(b.title));
});

const computedWarm = computed(() => {
  return data.value?.data?.itemsCountByColors
    ?.filter(item => warmTones.includes(item.title))
    ?.sort((a, b) => warmTones.indexOf(a.title) - warmTones.indexOf(b.title));
});

function getImage(title: string) {
  switch (title) {
    case 'Blue':
      return 'moto-gloves-polygon-factory-new';
    case 'Light Blue':
      return 'shadow-daggers-gamma-doppler-phase-1-factory-new';
    case 'Green':
      return 'bowie-knife-gamma-doppler-emerald-factory-new';
    case 'Purple':
      return 'talon-knife-ultraviolet-well-worn';
    case 'Gray':
      return 'classic-knife-night-stripe-factory-new';
    case 'Light Brown':
      return 'nova-dark-sigil-factory-new';
    case 'Light Green':
      return 'sport-gloves-hedge-maze-factory-new';
    case 'Light Gray':
      return 'skeleton-knife-urban-masked-factory-new';
    case 'Red':
      return 'sport-gloves-scarlet-shamagh-factory-new';
    case 'Pink':
      return 'usp-s-target-acquired-factory-new';
    case 'Orange':
      return 'p90-asiimov-factory-new';
    case 'Yellow':
      return 'mag-7-bulldozer-battle-scarred';
    case 'Light Yellow':
      return 'hand-wraps-caution-battle-scarred';
    case 'Maroon':
      return 'mp9-hot-rod-factory-new';
    case 'Bright Red':
      return 'kukri-knife-slaughter-field-tested';
    case 'Dark Orange':
      return 'five-seven-orange-peel-factory-new';
    case 'Peach':
      return 'awp-boom-factory-new';
    case 'Golden Brown':
      return 'p90-scarab-rush-factory-new';
    case 'Bright Yellow':
      return 'tec-9-fuel-injector-factory-new';
    case 'Dark Green':
      return 'aug-carved-jade-factory-new';
    case 'Lime':
      return 'p250-wingshot-factory-new';
    case 'Teal':
      return 'glock-18-off-world-factory-new';
    case 'Aqua':
      return 'famas-waters-of-nephthys-factory-new';
    case 'Navy Blue':
      return 'glock-18-blue-fissure-factory-new';
    case 'Royal Blue':
      return 'souvenir-usp-s-royal-blue-factory-new';
    case 'Violet':
      return 'five-seven-violent-daimyo-factory-new';
    case 'Magenta':
      return 'm4a1-s-flashback-factory-new';
    case 'Black':
      return 'ak-47-slate-factory-new';
    case 'Dark Gray':
      return 'ak-47-baroque-purple-factory-new';
    case 'Beige':
      return 'moto-gloves-transport-factory-new';
    case 'White':
      return 'mp9-arctic-tri-tone-factory-new';
    case 'Brown':
      return 'ak-47-cartel-factory-new';

    default:
      return 'ak-47-cartel-factory-new';
  }
}

const title = computed(() => {
  return t('color_group_page.seo_title');
});
const description = computed(() => {
  return t('color_group_page.seo_description');
});
const seoImage = computed(() => {
  return getImageFromWaxpeer('usp-s-target-acquired-factory-new', '1200:630');
});
const { alternateLinks, canonical } = useAlternateLinks(`/colors`);

useHead({
  title: () => title.value,
  meta: [
    {
      name: 'keywords',
      content: 'steam, counter strike global offensive, csgo, cs2, skins, items, colors, color groups',
    },
  ],
  link: () => alternateLinks.value,
});

useSeoMeta({
  title: () => title.value,
  ogTitle: () => title.value,
  description: () => description.value,
  ogDescription: () => description.value,
  ogImage: () => seoImage.value,
  twitterCard: 'summary_large_image',
  twitterTitle: () => title.value,
  twitterDescription: () => description.value,
  twitterImage: () => seoImage.value,
  ogSiteName: 'CSMarketCap',
  ogUrl: () => canonical.value,
  ogType: 'website',
  ogLocale: () => locale.value,
});

useSchemaOrg([
  defineItemList({
    id: `itemlist-${title.value.replace(/\s+/g, '-').toLowerCase()}-colors-page-${locale.value}`, // Уникальный ID для списка
    name: title.value,
    description: description.value,
    image: () => seoImage.value,
    itemListElement: data.value?.data?.itemsCountByColors?.map((item, index) => {
      return {
        '@type': 'ListItem',
        'position': index + 1,
        'item': {
          '@type': 'Thing',
          'name': item.title,
          'image': getImage(item.title),
        },
        'id': `item-${index + 1}-${item.title}`,
      };
    }),
  }),

  defineBreadcrumb({
    itemListElement: [
      { name: t('main'), item: '/' },
      { name: title.value, item: `${siteHost}/colors` },
    ],
  }),
]);
</script>

<style lang="scss" scoped>
.color-groups {
  padding-bottom: 100px;

  @media screen and (max-width: 1250px) {
    margin-top: 0;
  }

  &-info {
    margin-top: 4rem;
    width: 100%;
    display: flex;
    align-items: center;
    gap: 4.8rem;
    color: var(--surfaces-on-surface);

    &-image {
      width: 200px;
      height: 200px;
      flex-shrink: 0;

      img {
        object-fit: contain;
        object-position: center;
      }

    }

    @media screen and (max-width: 525px) {
      flex-direction: column;
      align-items: center;
      gap: 16px;

      &-image {
        display: flex;
        justify-content: center;
        width: 100%;
        min-width: 100%;
        margin-top: 10px;
      }
    }

    &-content {
      display: flex;
      flex-direction: column;
      gap: 20px;

      .content__header {
        display: flex;
        align-items: center;
        gap: 12px;

        &-name {
          font-weight: 600;
          font-size: 2.8rem;
          line-height: 3.8rem;
        }

        &-description {
          color: var(--surfaces-on-surface-variable);
          max-width: 70rem;
          font-size: 16px;
          font-weight: 400;
          line-height: 200%;
        }
      }
    }
  }

  &__items {
    margin-top: 5rem;

    h2 {
      font-size: 2.4rem;
      font-weight: 600;
      line-height: 133%;
      margin-bottom: 24px;
    }

    &-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
      gap: 16px;
    }
  }
}
</style>
